import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import GalleryCard from './GalleryCard';
import { supabase } from '../utils/supabaseClient';
import '../styles/GallerySearch.css';

const GallerySearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 12; // Number of galleries to load per page
  const [searchParams, setSearchParams] = useState({
    location: 'Chicago, IL',
    distance: 10,
    eventTimeFrame: 'any',
    sortOption: 'default',
    artTypeFilter: [],
    galleryTypeFilter: [],
    includeBookmarkedOnly: false,
    includeFollowedOnly: false
  });

  // Get current user if logged in
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    // Get the current auth user from Supabase
    const fetchUser = async () => {
      const { data } = await supabase.auth.getUser();
      setCurrentUser(data?.user || null);
    };

    fetchUser();
  }, []);

  // Parse query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newSearchParams = { ...searchParams };

    // Update search params from URL
    if (params.has('location')) newSearchParams.location = params.get('location');
    if (params.has('distance')) newSearchParams.distance = parseInt(params.get('distance'));
    if (params.has('eventTimeFrame')) newSearchParams.eventTimeFrame = params.get('eventTimeFrame');
    if (params.has('sortOption')) newSearchParams.sortOption = params.get('sortOption');
    if (params.has('artTypeFilter')) newSearchParams.artTypeFilter = params.get('artTypeFilter').split(',');
    if (params.has('galleryTypeFilter')) newSearchParams.galleryTypeFilter = params.get('galleryTypeFilter').split(',');
    if (params.has('includeBookmarkedOnly')) newSearchParams.includeBookmarkedOnly = params.get('includeBookmarkedOnly') === 'true';
    if (params.has('includeFollowedOnly')) newSearchParams.includeFollowedOnly = params.get('includeFollowedOnly') === 'true';

    setSearchParams(newSearchParams);
  }, [location.search]);

  // Fetch galleries from our Postgres function
  useEffect(() => {
    const fetchGalleries = async () => {
      setLoading(true);
      setOffset(0); // Reset offset when search params change
      
      try {
        // Get coordinates for the location
        const coordinates = await getCoordinatesForLocation(searchParams.location);
        if (!coordinates) {
          throw new Error('Could not determine coordinates for the provided location');
        }

        // Call the PostgreSQL function via the REST API
        const { data, error } = await supabase.rpc('get_nearby_galleries_enhanced', {
          user_lat: coordinates.lat,
          user_lng: coordinates.lng,
          radius: searchParams.distance,
          limit_val: LIMIT,
          offset_val: 0,
          sort_option: searchParams.sortOption,
          user_uuid_text: currentUser?.id || null,
          art_type_filter: searchParams.artTypeFilter.length > 0 ? searchParams.artTypeFilter : null,
          gallery_type_filter: searchParams.galleryTypeFilter.length > 0 ? searchParams.galleryTypeFilter : null,
          event_time_frame: searchParams.eventTimeFrame !== 'any' ? searchParams.eventTimeFrame : null,
          include_bookmarked_only: searchParams.includeBookmarkedOnly,
          include_followed_only: searchParams.includeFollowedOnly
        });

        if (error) {
          throw error;
        }

        // Update galleries state with the results
        setGalleries(data || []);
        setHasMore((data || []).length === LIMIT);
      } catch (error) {
        console.error('Error fetching galleries:', error);
        // Show error message
      } finally {
        setLoading(false);
      }
    };

    fetchGalleries();
  }, [searchParams, currentUser]);

  // Function to load more galleries
  const loadMoreGalleries = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    const newOffset = offset + LIMIT;
    
    try {
      // Get coordinates for the location
      const coordinates = await getCoordinatesForLocation(searchParams.location);
      if (!coordinates) {
        throw new Error('Could not determine coordinates for the provided location');
      }

      // Call the PostgreSQL function with the new offset
      const { data, error } = await supabase.rpc('get_nearby_galleries_enhanced', {
        user_lat: coordinates.lat,
        user_lng: coordinates.lng,
        radius: searchParams.distance,
        limit_val: LIMIT,
        offset_val: newOffset,
        sort_option: searchParams.sortOption,
        user_uuid_text: currentUser?.id || null,
        art_type_filter: searchParams.artTypeFilter.length > 0 ? searchParams.artTypeFilter : null,
        gallery_type_filter: searchParams.galleryTypeFilter.length > 0 ? searchParams.galleryTypeFilter : null,
        event_time_frame: searchParams.eventTimeFrame !== 'any' ? searchParams.eventTimeFrame : null,
        include_bookmarked_only: searchParams.includeBookmarkedOnly,
        include_followed_only: searchParams.includeFollowedOnly
      });

      if (error) {
        throw error;
      }

      // Update galleries state with the new results
      setGalleries(prevGalleries => [...prevGalleries, ...(data || [])]);
      setOffset(newOffset);
      setHasMore((data || []).length === LIMIT);
    } catch (error) {
      console.error('Error loading more galleries:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Update URL with search parameters
    const params = new URLSearchParams();
    params.set('location', searchParams.location);
    params.set('distance', searchParams.distance.toString());
    params.set('eventTimeFrame', searchParams.eventTimeFrame);
    params.set('sortOption', searchParams.sortOption);
    
    if (searchParams.artTypeFilter.length > 0) {
      params.set('artTypeFilter', searchParams.artTypeFilter.join(','));
    }
    
    if (searchParams.galleryTypeFilter.length > 0) {
      params.set('galleryTypeFilter', searchParams.galleryTypeFilter.join(','));
    }
    
    params.set('includeBookmarkedOnly', searchParams.includeBookmarkedOnly.toString());
    params.set('includeFollowedOnly', searchParams.includeFollowedOnly.toString());
    
    navigate(`/galleries?${params.toString()}`);
  };

  // Helper function to get coordinates from location string (in a real app, use a geocoding service)
  const getCoordinatesForLocation = async (locationString) => {
    // Placeholder for geocoding service
    // In a real app, use Google Maps Geocoding API or similar
    if (locationString.includes('Chicago')) {
      return { lat: 41.8781, lng: -87.6298 }; // Chicago coordinates
    } else {
      // Default coordinates if location not recognized
      return { lat: 40.7128, lng: -74.0060 }; // New York coordinates
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setSearchParams(prevParams => ({
      ...prevParams,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="gallery-search-container">
      {/* Hero Search Section */}
      <div className="search-container py-16 mb-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <img src="/images/inline-logo-white-1.png" alt="VibrantCast" className="h-12 mx-auto" />
          </div>
          <h1 className="text-4xl font-extrabold text-white mb-4">Find Art Near You</h1>
          <p className="text-xl text-white mb-8">Discover galleries, events, and exhibitions in immersive 3D</p>
          
          <form onSubmit={handleSearch} className="bg-white rounded-lg shadow-lg p-4">
            <div className="flex flex-wrap items-center">
              <div className="w-full md:w-1/3 mb-4 md:mb-0 md:pr-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input 
                  type="text" 
                  name="location"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                  placeholder="City or zip code" 
                  value={searchParams.location}
                  onChange={handleInputChange}
                />
              </div>
              <div className="w-full md:w-1/3 mb-4 md:mb-0 md:px-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Distance</label>
                <select 
                  name="distance"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={searchParams.distance}
                  onChange={handleInputChange}
                >
                  <option value="5">5 miles</option>
                  <option value="10">10 miles</option>
                  <option value="25">25 miles</option>
                  <option value="50">50 miles</option>
                </select>
              </div>
              <div className="w-full md:w-1/3 md:pl-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Events</label>
                <select 
                  name="eventTimeFrame"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={searchParams.eventTimeFrame}
                  onChange={handleInputChange}
                >
                  <option value="any">Any time</option>
                  <option value="today">Today</option>
                  <option value="this_weekend">This weekend</option>
                  <option value="this_week">This week</option>
                  <option value="this_month">This month</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4 flex justify-between">
              <button 
                type="button"
                className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-md"
                onClick={() => {
                  // Toggle advanced filters display
                  // Implement filter modal or expand additional filters
                }}
              >
                Advanced Filters
              </button>
              <button 
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Filter and Sort Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <div className="flex flex-wrap items-center justify-between">
          <div className="flex items-center mb-4 md:mb-0">
            <span className="text-gray-700 mr-2">Sort by:</span>
            <select 
              name="sortOption"
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
              value={searchParams.sortOption}
              onChange={handleInputChange}
            >
              <option value="default">Relevance</option>
              <option value="distance">Distance</option>
              <option value="upcoming_events">Upcoming events</option>
              <option value="popular">Popular</option>
              <option value="artwork_count">Artwork Count</option>
              <option value="gallery_type">Gallery Type</option>
              {currentUser && <option value="personal">Personal Relevance</option>}
            </select>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {searchParams.galleryTypeFilter.map(filter => (
              <button 
                key={filter}
                className="tag-pill flex items-center"
                onClick={() => {
                  setSearchParams(prev => ({
                    ...prev,
                    galleryTypeFilter: prev.galleryTypeFilter.filter(item => item !== filter)
                  }));
                }}
              >
                <span>{filter}</span>
                <span className="ml-1">×</span>
              </button>
            ))}
            
            {searchParams.artTypeFilter.map(filter => (
              <button 
                key={filter}
                className="tag-pill flex items-center"
                onClick={() => {
                  setSearchParams(prev => ({
                    ...prev,
                    artTypeFilter: prev.artTypeFilter.filter(item => item !== filter)
                  }));
                }}
              >
                <span>{filter}</span>
                <span className="ml-1">×</span>
              </button>
            ))}
            
            <button className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
              + More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Search Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <h2 className="text-xl font-bold text-gray-900 mb-6">
          {loading 
            ? 'Loading galleries...' 
            : galleries.length === 0
              ? 'No galleries found near this location'
              : `Found ${galleries.length}${!hasMore ? '' : '+'} galleries near ${searchParams.location}`}
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            // Loading placeholders
            Array(6).fill().map((_, index) => (
              <div key={index} className="gallery-card bg-white rounded-lg shadow overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200"></div>
                <div className="p-4">
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                    <div className="h-6 bg-gray-200 rounded w-20"></div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            ))
          ) : (
            galleries.map(gallery => (
              <GalleryCard 
                key={gallery.gallery_id} 
                gallery={gallery} 
                currentUser={currentUser}
              />
            ))
          )}
        </div>
        
        {galleries.length > 0 && hasMore && (
          <div className="flex justify-center mt-8">
            <button 
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={loadMoreGalleries}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load More Galleries'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GallerySearch;